import { useEffect, useMemo, useState } from "react";
import {
  Users, KeyRound, Trash2, Plus, Copy, Shield,
  ChevronDown, ChevronRight, Check, X,
} from "lucide-react";
import api, { formatErr } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";

/**
 * Iter 231 — Customer-portal self-service team management.
 *
 * Lets a logged-in client (or staff in preview mode) list / invite /
 * remove / reset-password their teammates that share the same
 * `customer_id`, with one of 5 access presets:
 *   • Owner       — full access
 *   • Admin       — full access except team management
 *   • Accounts    — quotes/invoices/documents only
 *   • Maintenance — jobs/tickets/photos/orders
 *   • Custom      — tick exactly what they can see
 *
 * Trust Contract: NO Modal usage, NO inline window prompts. Inline panels + 2-step
 * confirm in-row only.
 *
 * Routes (all defended by `lib/portal_perms.assert_portal_perm` server-side):
 *   GET    /api/portal/perm-catalogue
 *   GET    /api/portal/users
 *   POST   /api/portal/users               { email, name?, password?, portal_preset, portal_permissions? }
 *   POST   /api/portal/users/{uid}/set-password   { new_password }
 *   PATCH  /api/portal/users/{uid}/permissions    { portal_preset, portal_permissions? }
 *   DELETE /api/portal/users/{uid}
 */

const PERMISSION_GROUPS = [
  {
    label: "Work",
    keys: [
      "portal.jobs.read",
      "portal.tickets.read",
      "portal.tickets.write",
      "portal.photos.read",
      // Iter 394b — Dirk: "we need them by default to have access to
      // the logs, not to the performance. And then logs and performance
      // must be a custom attribute which can be given separately."
      // Two independent perms so maintenance techs can log daily
      // readings without seeing analytics/trends. Owner grants the
      // analytics one explicitly when needed.
      "portal.performance.capture",
      "portal.performance.read",
    ],
  },
  {
    label: "Finance",
    keys: [
      "portal.quotes.read",
      "portal.invoices.read",
    ],
  },
  {
    // [Iter 349.q — Dirk, 2026-02-27] Per-folder document access.
    // Owner defaults each folder to a role; per-user override via
    // the "Custom" preset toggles below.
    label: "Documents (per folder)",
    keys: [
      "portal.docs.contracts.read",
      "portal.docs.proposals.read",
      "portal.docs.manuals.read",
      "portal.docs.warranties.read",
      "portal.docs.account.read",
      "portal.docs.notices.read",
      "portal.docs.photos.read",
      "portal.docs.compliance.read",
      "portal.docs.reports.read",
    ],
  },
  {
    label: "Profile & orders",
    keys: [
      "portal.profile.read",
      "portal.profile.write",
      "portal.orders.write",
      "portal.users.manage",
      "portal.announcements.read",
      "portal.documents.read",  // legacy generic — kept for uncatalogued doc types
    ],
  },
];

const PERM_LABELS = {
  "portal.jobs.read": "View jobs",
  "portal.tickets.read": "View tickets",
  "portal.tickets.write": "Log / reply tickets",
  "portal.photos.read": "View photos",
  "portal.quotes.read": "View quotes",
  "portal.invoices.read": "View invoices",
  "portal.documents.read": "View documents (uncatalogued fallback)",
  "portal.profile.read": "View profile",
  "portal.profile.write": "Edit profile",
  "portal.orders.write": "Place orders",
  "portal.users.manage": "Manage team",
  "portal.performance.capture": "Log sheet — daily readings",
  "portal.performance.read": "Performance analytics + reports",
  "portal.announcements.read": "View announcements",
  // [Iter 349.q] Per-folder document perms
  "portal.docs.contracts.read":   "Contracts & SLAs",
  "portal.docs.proposals.read":   "Proposals & Designs",
  "portal.docs.manuals.read":     "Manuals & Datasheets",
  "portal.docs.warranties.read":  "Warranties",
  "portal.docs.account.read":     "Account Docs",
  "portal.docs.notices.read":     "Notices & Bulletins",
  "portal.docs.photos.read":      "Photos & Site Plans",
  "portal.docs.compliance.read":  "Compliance (SHEQ / Insurance / Permits)",
  "portal.docs.reports.read":     "Reports & Technical",
};

const PRESET_DESCRIPTIONS = {
  owner: "Full access — sees every doc folder + manages team",
  admin: "Full access except team management",
  accounts: "Quotes, invoices, Account Docs + notices",
  maintenance: "Jobs, tickets, photos, orders, log sheet + Manuals / Warranties / Photos / Notices",
  custom: "Tick exactly what they can see (incl. per-folder document access)",
};

const PRESET_LABELS = {
  owner: "Owner",
  admin: "Admin",
  accounts: "Accounts",
  maintenance: "Maintenance",
  custom: "Custom",
};

const PRESET_ORDER = ["owner", "admin", "accounts", "maintenance", "custom"];

const EMPTY_CUSTOM = Object.fromEntries(
  Object.keys(PERM_LABELS).map((k) => [k, false]),
);

export default function PortalTeamBlock({
  currentUserId,
  defaultOpen = false,
  isPreview = false,
  customerId,
}) {
  const { user: me } = useAuth();
  const [open, setOpen] = useState(defaultOpen);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [showAdd, setShowAdd] = useState(false);

  // Add form state
  const [emailInput, setEmailInput] = useState("");
  const [nameInput, setNameInput] = useState("");
  const [pwdInput, setPwdInput] = useState("");
  const [presetInput, setPresetInput] = useState("maintenance");
  const [customInput, setCustomInput] = useState(EMPTY_CUSTOM);
  // Iter 427.p41 — Preflight lookup so we hide the password field for
  // identities that already exist (one identity = one password).
  const [emailLookup, setEmailLookup] = useState(null);

  const [tempCreds, setTempCreds] = useState(null);

  // Per-row state
  const [resetForUid, setResetForUid] = useState(null);
  const [resetPwd, setResetPwd] = useState("");
  const [confirmDeleteUid, setConfirmDeleteUid] = useState(null);
  const [editPresetUid, setEditPresetUid] = useState(null);
  const [editPresetDraft, setEditPresetDraft] = useState("maintenance");
  const [editCustomDraft, setEditCustomDraft] = useState(EMPTY_CUSTOM);
  // 2026-02 — Self-service change password (own account only).
  const [selfChangeOpen, setSelfChangeOpen] = useState(false);
  const [selfCurrentPwd, setSelfCurrentPwd] = useState("");
  const [selfNewPwd, setSelfNewPwd] = useState("");
  const [selfNewPwd2, setSelfNewPwd2] = useState("");
  const [selfMsg, setSelfMsg] = useState("");

  // 2026-02 — Team-management is preset-gated. Non-managers see the
  // roster read-only and can only self-serve their own password change.
  const canManage = useMemo(() => {
    if (!me) return false;
    if (me.role !== "client") return true;
    const eff = me.effective_perms || {};
    if (!me.portal_preset && Object.keys(eff).length === 0) return true;
    return Boolean(eff["portal.users.manage"]);
  }, [me]);

  const listUrl = isPreview ? `/customers/${customerId}/portal-users` : "/portal/users";

  const refresh = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(listUrl);
      setUsers(data || []);
    } catch (e) {
      setErr(formatErr(e?.response?.data?.detail, "Could not load team"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, listUrl]);

  const preflightEmail = async () => {
    const email = (emailInput || "").trim().toLowerCase();
    if (!email || !/@/.test(email)) {
      setEmailLookup(null);
      return;
    }
    try {
      const { data } = await api.get(
        `/portal/users/lookup?email=${encodeURIComponent(email)}`,
      );
      setEmailLookup(data);
      if (data?.exists && data?.has_password) setPwdInput("");
    } catch (_) {
      setEmailLookup(null);
    }
  };

  const submitAdd = async () => {
    if (!emailInput) { setErr("Email is required."); return; }
    if (emailLookup?.exists && emailLookup?.already_on_this_customer) {
      setErr("This email is already a portal user for your customer.");
      return;
    }
    if (emailLookup?.exists && emailLookup?.is_staff) {
      setErr("This email belongs to a staff account and can't be added.");
      return;
    }
    if (pwdInput && pwdInput.length < 4) {
      setErr("Password must be at least 4 characters (or leave blank).");
      return;
    }
    setBusy(true);
    setErr("");
    try {
      const body = {
        email: emailInput,
        name: nameInput || undefined,
        portal_preset: presetInput,
      };
      if (presetInput === "custom") body.portal_permissions = customInput;
      // Iter 427.p41 — never send password on cross-customer add.
      if (pwdInput && !emailLookup?.exists) body.password = pwdInput;
      const { data } = await api.post("/portal/users", body);
      setTempCreds({
        email: data.email,
        password: data?.cross_customer_add ? null : (pwdInput || null),
        resent: !!data.resent,
        crossCustomer: !!data?.cross_customer_add,
      });
      setShowAdd(false);
      setEmailInput("");
      setNameInput("");
      setPwdInput("");
      setPresetInput("maintenance");
      setCustomInput(EMPTY_CUSTOM);
      setEmailLookup(null);
      await refresh();
    } catch (e) {
      setErr(formatErr(e?.response?.data?.detail, "Could not add teammate"));
    } finally {
      setBusy(false);
    }
  };

  const submitReset = async () => {
    if (!resetForUid || resetPwd.length < 4) {
      setErr("Password must be at least 4 characters.");
      return;
    }
    setBusy(true);
    setErr("");
    try {
      await api.post(`/portal/users/${resetForUid}/set-password`, { new_password: resetPwd });
      const u = users.find((x) => x.id === resetForUid);
      setTempCreds({ email: u?.email, password: resetPwd });
      setResetForUid(null);
      setResetPwd("");
    } catch (e) {
      setErr(formatErr(e?.response?.data?.detail, "Could not reset password"));
    } finally {
      setBusy(false);
    }
  };

  const savePerms = async (uid) => {
    setBusy(true);
    setErr("");
    try {
      const body = { portal_preset: editPresetDraft };
      if (editPresetDraft === "custom") body.portal_permissions = editCustomDraft;
      await api.patch(`/portal/users/${uid}/permissions`, body);
      setEditPresetUid(null);
      await refresh();
    } catch (e) {
      setErr(formatErr(e?.response?.data?.detail, "Could not update permissions"));
    } finally {
      setBusy(false);
    }
  };

  const removeUser = async (uid) => {
    setBusy(true);
    setErr("");
    try {
      await api.delete(`/portal/users/${uid}`);
      setConfirmDeleteUid(null);
      await refresh();
    } catch (e) {
      setErr(formatErr(e?.response?.data?.detail, "Could not remove teammate"));
    } finally {
      setBusy(false);
    }
  };

  const submitSelfChangePwd = async () => {
    setSelfMsg("");
    setErr("");
    if (!selfCurrentPwd) { setErr("Enter your current password."); return; }
    if (selfNewPwd.length < 4) { setErr("New password must be at least 4 characters."); return; }
    if (selfNewPwd !== selfNewPwd2) { setErr("New passwords don't match."); return; }
    setBusy(true);
    try {
      await api.post("/auth/change-password", {
        current_password: selfCurrentPwd,
        new_password: selfNewPwd,
      });
      setSelfMsg("Password updated. Use the new one next time you sign in.");
      setSelfCurrentPwd("");
      setSelfNewPwd("");
      setSelfNewPwd2("");
      setSelfChangeOpen(false);
    } catch (e) {
      setErr(formatErr(e?.response?.data?.detail, "Could not change password"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="hv-card p-4" data-testid="portal-team-block">
      <header
        className="flex items-center justify-between cursor-pointer"
        onClick={() => setOpen((v) => !v)}
      >
        <div className="flex items-center gap-2">
          {open ? <ChevronDown className="w-4 h-4 text-[#647087]" /> : <ChevronRight className="w-4 h-4 text-[#647087]" />}
          <Users className="w-4 h-4 text-[#1fb6c1]" />
          <h3 className="font-display text-base font-bold">Team access</h3>
          <span className="text-[10px] font-mono px-2 py-0.5 rounded-full border border-[#1fb6c1]/40 text-[#1fb6c1] bg-[#1fb6c1]/5">
            {users.length || "—"}
          </span>
          {isPreview && (
            <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded border border-amber-400/30 text-amber-300 bg-amber-400/5">
              Preview · read-only
            </span>
          )}
          {open && !canManage && !isPreview && (
            <div
              className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded border border-white/10 text-[#647087] bg-white/[0.02]"
              data-testid="portal-team-view-only-badge"
            >
              View only · reset your own password
            </div>
          )}
        </div>
      </header>

      {open && (
        <div className="mt-3 space-y-3" data-testid="portal-team-panel">
          <div className="text-xs text-[#a0aabf]">
            {canManage
              ? "Add people from your company and pick a preset — each preset gives the right slice of access."
              : "Your account's team roster. You can update your own password below."}
          </div>

          {tempCreds && (
            <div className="hv-card p-3 space-y-2 border border-[#1fb6c1]/40 bg-[#1fb6c1]/5" data-testid="portal-team-creds">
              <div className="text-xs text-[#1fb6c1] uppercase tracking-wider font-bold">
                {tempCreds.resent
                  ? "Invite email resent"
                  : tempCreds.password
                    ? "Share these credentials securely"
                    : "Access ready · invite emailed"}
              </div>
              <CredRow label="Email" value={tempCreds.email} />
              {tempCreds.password ? (
                <CredRow label="Password" value={tempCreds.password} testId="portal-team-creds-pwd" />
              ) : (
                <p className="text-xs text-[#a0aabf]">
                  {tempCreds.resent
                    ? "We resent the sign-in email. They can also set their password on first sign-in."
                    : "No password set — an invite email was sent. They'll create their own password on first sign-in."}
                </p>
              )}
              <button
                onClick={() => setTempCreds(null)}
                className="text-[10px] uppercase tracking-wider text-[#647087] hover:text-white"
              >Dismiss</button>
            </div>
          )}

          {err && <div className="text-xs text-rose-300" data-testid="portal-team-err">{err}</div>}
          {selfMsg && (
            <div
              className="text-xs text-emerald-300 border border-emerald-500/30 bg-emerald-500/5 rounded px-2 py-1.5"
              data-testid="portal-team-self-msg"
            >
              {selfMsg}
            </div>
          )}

          <div className="space-y-2" data-testid="portal-team-list">
            {loading ? (
              <div className="text-xs text-[#647087]">Loading…</div>
            ) : users.length === 0 ? (
              <div className="text-xs text-[#647087] italic">No teammates yet.</div>
            ) : (
              users.map((u) => (
                <TeamRow
                  key={u.id}
                  user={u}
                  isSelf={u.id === currentUserId}
                  isPreview={isPreview}
                  canManage={canManage}
                  selfChangeOpen={selfChangeOpen && u.id === currentUserId}
                  selfCurrentPwd={selfCurrentPwd}
                  selfNewPwd={selfNewPwd}
                  selfNewPwd2={selfNewPwd2}
                  onOpenSelfChange={() => {
                    setSelfChangeOpen(true);
                    setSelfCurrentPwd(""); setSelfNewPwd(""); setSelfNewPwd2("");
                    setErr(""); setSelfMsg("");
                  }}
                  onCancelSelfChange={() => {
                    setSelfChangeOpen(false);
                    setSelfCurrentPwd(""); setSelfNewPwd(""); setSelfNewPwd2("");
                    setErr("");
                  }}
                  onChangeSelfCurrent={setSelfCurrentPwd}
                  onChangeSelfNew={setSelfNewPwd}
                  onChangeSelfNew2={setSelfNewPwd2}
                  onSubmitSelfChange={submitSelfChangePwd}
                  busy={busy}
                  resetForUid={resetForUid}
                  resetPwd={resetPwd}
                  confirmDeleteUid={confirmDeleteUid}
                  editPresetUid={editPresetUid}
                  editPresetDraft={editPresetDraft}
                  editCustomDraft={editCustomDraft}
                  onAskReset={() => { setResetForUid(u.id); setResetPwd(""); setErr(""); }}
                  onCancelReset={() => { setResetForUid(null); setResetPwd(""); }}
                  onChangeResetPwd={setResetPwd}
                  onSubmitReset={submitReset}
                  onAskDelete={() => setConfirmDeleteUid(u.id)}
                  onCancelDelete={() => setConfirmDeleteUid(null)}
                  onConfirmDelete={() => removeUser(u.id)}
                  onEditPerms={(initialPreset, initialCustom) => {
                    setEditPresetUid(u.id);
                    setEditPresetDraft(initialPreset);
                    setEditCustomDraft(initialCustom);
                  }}
                  onChangePresetDraft={setEditPresetDraft}
                  onChangeCustomDraft={setEditCustomDraft}
                  onSavePerms={() => savePerms(u.id)}
                  onCancelEditPerms={() => setEditPresetUid(null)}
                />
              ))
            )}
          </div>

          {!isPreview && canManage && (showAdd ? (
            <div className="hv-card p-3 space-y-3 border border-[#1fb6c1]/30 bg-[#1fb6c1]/[0.03]" data-testid="portal-team-add-form">
              <div className="text-xs uppercase tracking-wider text-[#1fb6c1] font-bold">Invite a teammate</div>
              <div className="grid sm:grid-cols-2 gap-2">
                <Field label="Email *">
                  <input type="email" required value={emailInput} onChange={(e) => { setEmailInput(e.target.value); setEmailLookup(null); }} onBlur={preflightEmail} className="hv-input" placeholder="teammate@company.com" data-testid="portal-team-add-email" />
                </Field>
                <Field label="Display name">
                  <input value={nameInput} onChange={(e) => setNameInput(e.target.value)} className="hv-input" placeholder="Optional" data-testid="portal-team-add-name" />
                </Field>
              </div>
              {emailLookup?.exists && emailLookup?.already_on_this_customer && (
                <div className="text-xs px-3 py-2 rounded border border-rose-500/40 bg-rose-500/10 text-rose-200" data-testid="portal-team-add-already-member">
                  This email is already a portal user for your customer.
                </div>
              )}
              {emailLookup?.exists && emailLookup?.is_staff && (
                <div className="text-xs px-3 py-2 rounded border border-rose-500/40 bg-rose-500/10 text-rose-200" data-testid="portal-team-add-staff-email">
                  This email belongs to a staff account and cannot be added as a portal user.
                </div>
              )}
              {emailLookup?.exists && !emailLookup?.already_on_this_customer && !emailLookup?.is_staff && (
                <div className="text-xs px-3 py-2 rounded border border-[#1fb6c1]/40 bg-[#1fb6c1]/10 text-[#a0e8ee]" data-testid="portal-team-add-existing-identity">
                  This email already has a Hidroverse account{emailLookup.name ? ` (${emailLookup.name})` : ""}. They&rsquo;ll use their existing password and pick your customer at the login screen. A single identity has ONE password across every customer they belong to.
                </div>
              )}
              <PresetPicker
                value={presetInput}
                onChange={setPresetInput}
                testIdPrefix="portal-team-add-preset"
              />
              {presetInput === "custom" && (
                <CustomPermsEditor
                  value={customInput}
                  onChange={setCustomInput}
                  testIdPrefix="portal-team-add-custom"
                />
              )}
              {!(emailLookup?.exists && emailLookup?.has_password) && (
                <Field label="Password (optional)">
                  <input type="text" value={pwdInput} onChange={(e) => setPwdInput(e.target.value)} className="hv-input font-mono" placeholder="Leave blank — they'll set their own" data-testid="portal-team-add-pwd" />
                </Field>
              )}
              <div className="flex gap-2">
                <button onClick={submitAdd} disabled={busy || !emailInput} className="hv-btn hv-btn-primary flex-1" data-testid="portal-team-add-submit">
                  <KeyRound className="w-4 h-4" /> {busy ? "Creating…" : "Invite teammate"}
                </button>
                <button onClick={() => setShowAdd(false)} className="hv-btn hv-btn-ghost">Cancel</button>
              </div>
            </div>
          ) : (
            <button
              onClick={() => { setShowAdd(true); setErr(""); }}
              className="hv-btn hv-btn-ghost w-full justify-center"
              data-testid="portal-team-add-btn"
            ><Plus className="w-4 h-4" /> Invite teammate</button>
          ))}
        </div>
      )}
    </section>
  );
}

/* ---------------- Row ---------------- */
function TeamRow({
  user, isSelf, isPreview, busy, canManage,
  selfChangeOpen, selfCurrentPwd, selfNewPwd, selfNewPwd2,
  onOpenSelfChange, onCancelSelfChange,
  onChangeSelfCurrent, onChangeSelfNew, onChangeSelfNew2, onSubmitSelfChange,
  resetForUid, resetPwd, confirmDeleteUid, editPresetUid,
  editPresetDraft, editCustomDraft,
  onAskReset, onCancelReset, onChangeResetPwd, onSubmitReset,
  onAskDelete, onCancelDelete, onConfirmDelete,
  onEditPerms, onChangePresetDraft, onChangeCustomDraft, onSavePerms, onCancelEditPerms,
}) {
  const preset = user.portal_preset || "owner";  // legacy clients default to owner
  const eff = user.effective_perms || {};
  const editing = editPresetUid === user.id;
  const resetting = resetForUid === user.id;
  const confirmDel = confirmDeleteUid === user.id;
  const grantedCount = useMemo(
    () => Object.values(eff).filter(Boolean).length,
    [eff],
  );

  return (
    <div
      className="hv-card p-3 space-y-2"
      data-testid={`portal-team-user-${user.id}`}
    >
      <div className="flex items-center gap-3 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="font-medium truncate">
            {user.name}
            {isSelf && (
              <span className="ml-2 text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded border border-[#A78BFA]/40 text-[#A78BFA] bg-[#A78BFA]/5">You</span>
            )}
          </div>
          <div className="text-xs text-[#647087] font-mono truncate">{user.email}</div>
          <div className="mt-1.5 flex items-center gap-2 flex-wrap">
            <span
              className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border border-[#1fb6c1]/40 text-[#1fb6c1] bg-[#1fb6c1]/5"
              data-testid={`portal-team-preset-${user.id}`}
            >
              {PRESET_LABELS[preset] || preset}
            </span>
            <span className="text-[10px] uppercase tracking-wider text-[#647087]">
              {grantedCount} / 11 perms
            </span>
          </div>
        </div>
        {!isPreview && !editing && !resetting && !confirmDel && !selfChangeOpen && (
          <div className="flex items-center gap-2 flex-wrap">
            {/* 2026-02 — Every portal user can change THEIR OWN password
                via self-service (needs current password). Non-managers
                see this button ONLY on their own row. */}
            {isSelf && (
              <button
                onClick={onOpenSelfChange}
                className="hv-btn hv-btn-ghost text-xs"
                data-testid={`portal-team-self-change-${user.id}`}
              ><KeyRound className="w-3 h-3" /> Change my password</button>
            )}
            {canManage && (
              <>
                <button
                  onClick={() => onEditPerms(preset, preset === "custom" ? eff : { ...EMPTY_CUSTOM })}
                  className="hv-btn hv-btn-ghost text-xs"
                  data-testid={`portal-team-perms-${user.id}`}
                  title="Edit access"
                ><Shield className="w-3 h-3" /> Permissions</button>
                {!isSelf && (
                  <button
                    onClick={onAskReset}
                    className="hv-btn hv-btn-ghost text-xs"
                    data-testid={`portal-team-reset-${user.id}`}
                  ><KeyRound className="w-3 h-3" /> Reset</button>
                )}
                {!isSelf && (
                  <button
                    onClick={onAskDelete}
                    disabled={busy}
                    className="hv-btn hv-btn-ghost text-xs text-rose-300 hover:!text-rose-200"
                    data-testid={`portal-team-remove-${user.id}`}
                  ><Trash2 className="w-3 h-3" /> Remove</button>
                )}
              </>
            )}
          </div>
        )}
      </div>

      {/* Inline self-change-password (own account, requires current pwd) */}
      {selfChangeOpen && (
        <div
          className="border border-[#1fb6c1]/30 bg-[#1fb6c1]/[0.03] rounded p-3 space-y-2"
          data-testid={`portal-team-self-change-panel-${user.id}`}
        >
          <div className="text-[10px] uppercase tracking-wider text-[#1fb6c1] font-bold">
            Change your password
          </div>
          <input
            autoFocus
            type="password"
            placeholder="Current password"
            value={selfCurrentPwd}
            onChange={(e) => onChangeSelfCurrent(e.target.value)}
            className="hv-input text-sm"
            data-testid={`portal-team-self-current-${user.id}`}
          />
          <input
            type="password"
            placeholder="New password (min 4 chars)"
            value={selfNewPwd}
            onChange={(e) => onChangeSelfNew(e.target.value)}
            className="hv-input text-sm"
            data-testid={`portal-team-self-new-${user.id}`}
          />
          <input
            type="password"
            placeholder="Confirm new password"
            value={selfNewPwd2}
            onChange={(e) => onChangeSelfNew2(e.target.value)}
            className="hv-input text-sm"
            data-testid={`portal-team-self-new2-${user.id}`}
          />
          <div className="flex gap-2 justify-end">
            <button onClick={onCancelSelfChange} className="hv-btn hv-btn-ghost text-xs">Cancel</button>
            <button
              onClick={onSubmitSelfChange}
              disabled={busy}
              className="hv-btn hv-btn-primary text-xs"
              data-testid={`portal-team-self-change-submit-${user.id}`}
            >Save new password</button>
          </div>
        </div>
      )}

      {/* Inline reset-password */}
      {resetting && (
        <div className="border border-white/10 rounded p-2 flex items-center gap-2 flex-wrap" data-testid={`portal-team-reset-panel-${user.id}`}>
          <input
            autoFocus
            type="text"
            placeholder="New password"
            value={resetPwd}
            onChange={(e) => onChangeResetPwd(e.target.value)}
            className="hv-input text-sm flex-1 min-w-[140px]"
          />
          <button onClick={onSubmitReset} disabled={busy} className="hv-btn hv-btn-primary text-xs" data-testid={`portal-team-reset-confirm-${user.id}`}>Save</button>
          <button onClick={onCancelReset} className="hv-btn hv-btn-ghost text-xs">Cancel</button>
        </div>
      )}

      {/* Inline confirm-delete */}
      {confirmDel && (
        <div
          className="border border-rose-500/40 bg-rose-500/5 rounded p-2 flex items-center justify-between gap-2 flex-wrap"
          data-testid={`portal-team-confirm-delete-${user.id}`}
        >
          <div className="text-xs">
            Remove portal access for <strong>{user.email}</strong>?
          </div>
          <div className="flex gap-2">
            <button onClick={onCancelDelete} className="hv-btn hv-btn-ghost text-xs">Cancel</button>
            <button
              onClick={onConfirmDelete}
              disabled={busy}
              className="hv-btn text-xs bg-rose-500/80 hover:bg-rose-500 text-white border border-rose-500/60"
              data-testid={`portal-team-confirm-delete-yes-${user.id}`}
            >Yes, remove</button>
          </div>
        </div>
      )}

      {/* Inline edit-preset */}
      {editing && (
        <div className="border border-white/10 rounded p-2 space-y-3" data-testid={`portal-team-edit-panel-${user.id}`}>
          <PresetPicker
            value={editPresetDraft}
            onChange={onChangePresetDraft}
            testIdPrefix={`portal-team-edit-preset-${user.id}`}
            compact
          />
          {editPresetDraft === "custom" && (
            <CustomPermsEditor
              value={editCustomDraft}
              onChange={onChangeCustomDraft}
              testIdPrefix={`portal-team-edit-custom-${user.id}`}
            />
          )}
          <div className="flex gap-2 justify-end">
            <button onClick={onCancelEditPerms} className="hv-btn hv-btn-ghost text-xs">Cancel</button>
            <button onClick={onSavePerms} disabled={busy} className="hv-btn hv-btn-primary text-xs" data-testid={`portal-team-perms-save-${user.id}`}>Save</button>
          </div>
        </div>
      )}

      {/* Effective-perm chips — always visible (read-only) */}
      {!editing && (
        <div className="flex items-center gap-1.5 flex-wrap" data-testid={`portal-team-perm-chips-${user.id}`}>
          {Object.keys(PERM_LABELS).map((k) => {
            const active = !!eff[k];
            return (
              <span
                key={k}
                title={PERM_LABELS[k]}
                data-testid={`portal-team-perm-${user.id}-${k}`}
                className={`text-[9px] uppercase tracking-wider font-mono px-1.5 py-0.5 rounded border inline-flex items-center gap-1 ${active ? "" : "opacity-30"}`}
                style={{
                  borderColor: active ? "rgba(31,182,193,0.55)" : "rgba(255,255,255,0.08)",
                  background: active ? "rgba(31,182,193,0.1)" : "rgba(255,255,255,0.02)",
                  color: active ? "#1fb6c1" : "#647087",
                }}
              >
                {active ? <Check className="w-2.5 h-2.5" /> : <X className="w-2.5 h-2.5" />}
                {PERM_LABELS[k]}
              </span>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ---------------- Sub-components ---------------- */
function PresetPicker({ value, onChange, testIdPrefix, compact = false }) {
  return (
    <div className="space-y-1.5">
      <div className="text-[10px] uppercase tracking-wider text-[#a0aabf]">
        Access preset
      </div>
      <div className={`grid gap-1.5 ${compact ? "sm:grid-cols-5 grid-cols-2" : "sm:grid-cols-5 grid-cols-2"}`}>
        {PRESET_ORDER.map((p) => (
          <button
            key={p}
            type="button"
            onClick={() => onChange(p)}
            className={`hv-card p-2 text-left transition ${
              value === p
                ? "border-[#1fb6c1] bg-[#1fb6c1]/10"
                : "border-white/10 hover:border-white/20"
            }`}
            data-testid={`${testIdPrefix}-${p}`}
          >
            <div className="text-[10px] font-bold uppercase tracking-wider">
              {PRESET_LABELS[p]}
            </div>
            <div className="text-[9px] text-[#a0aabf] mt-0.5 leading-tight">
              {PRESET_DESCRIPTIONS[p]}
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

function CustomPermsEditor({ value, onChange, testIdPrefix }) {
  const toggle = (k) => onChange({ ...value, [k]: !value[k] });
  return (
    <div className="space-y-2.5" data-testid={testIdPrefix}>
      <div className="text-[10px] uppercase tracking-wider text-[#a0aabf]">
        Pick exactly what they can access
      </div>
      {PERMISSION_GROUPS.map((g) => (
        <div key={g.label}>
          <div className="text-[10px] uppercase tracking-wider text-[#647087] mb-1">
            {g.label}
          </div>
          <div className="grid sm:grid-cols-2 gap-1">
            {g.keys.map((k) => (
              <label
                key={k}
                className="flex items-center gap-1.5 text-xs cursor-pointer hover:bg-white/[0.02] p-1 rounded"
                data-testid={`${testIdPrefix}-${k}`}
              >
                <input
                  type="checkbox"
                  checked={Boolean(value[k])}
                  onChange={() => toggle(k)}
                  className="rounded border-white/20"
                />
                <span>{PERM_LABELS[k]}</span>
              </label>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-wider text-[#a0aabf] block mb-1">
        {label}
      </label>
      {children}
    </div>
  );
}

function CredRow({ label, value, testId }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-wider text-[#647087]">{label}</div>
        <div className="font-mono text-sm truncate" data-testid={testId}>{value}</div>
      </div>
      <button
        onClick={() => navigator.clipboard.writeText(value || "")}
        className="hv-btn hv-btn-ghost text-xs"
        aria-label={`Copy ${label}`}
      ><Copy className="w-3 h-3" /></button>
    </div>
  );
}
